import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

export type RegistryScopeTable =
  | 'workspace_slide_registered_pages'
  | 'workspace_slide_registered_layouts'
  | 'workspace_worksheet_registered_pages';

export type RegistryScopeNoun = '페이지' | '레이아웃' | '활동지레이아웃';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createRegistryItemId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (c) => {
    const n = Number(c);
    const r = Math.floor(Math.random() * 16);
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

export function isRegistryItemId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

export function dedupeRegistryItemIds(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  return ids.map((raw) => {
    const trimmed = String(raw ?? '').trim();
    let id = isRegistryItemId(trimmed) ? trimmed : createRegistryItemId();
    if (seen.has(id)) {
      id = createRegistryItemId();
    }
    seen.add(id);
    return id;
  });
}

export function isSupabaseUniqueViolation(error: PostgrestError | null | undefined): boolean {
  return error?.code === '23505';
}

export function formatRegistryScopeSaveError(
  error: PostgrestError | null | undefined,
  noun: RegistryScopeNoun
): string {
  const label =
    noun === '활동지레이아웃' ? '활동지 레이아웃' : noun === '레이아웃' ? '슬라이드 레이아웃' : '슬라이드 페이지';
  if (!error) return `${label} 등록 저장에 실패했습니다.`;
  if (error.code === '23505') {
    return `${label} 등록이 기존 데이터와 충돌했습니다. 잠시 후 다시 저장해 주세요.`;
  }
  if (error.code === '42501') {
    return `${label} 등록을 저장할 권한이 없습니다.`;
  }
  return error.message || `${label} 등록 저장에 실패했습니다.`;
}

const REGISTRY_SCOPE_DELETE_MAX_ATTEMPTS = 3;
const REGISTRY_SCOPE_INSERT_MAX_ATTEMPTS = 3;

export async function clearRegistryScopeRows(
  table: RegistryScopeTable,
  workspaceId: string,
  noun: RegistryScopeNoun
): Promise<void> {
  const sid = workspaceId.trim();
  if (!sid) return;

  for (let attempt = 0; attempt < REGISTRY_SCOPE_DELETE_MAX_ATTEMPTS; attempt += 1) {
    const { error: deleteError } = await supabase.from(table).delete().eq('workspace_id', sid);
    if (deleteError) throw new Error(formatRegistryScopeSaveError(deleteError, noun));

    const { count, error: countError } = await supabase
      .from(table)
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', sid);
    if (countError) throw new Error(formatRegistryScopeSaveError(countError, noun));
    if ((count ?? 0) === 0) return;
  }

  const label = noun === '활동지레이아웃' ? '활동지 레이아웃' : `슬라이드 ${noun}`;
  throw new Error(
    `기존 ${label} 등록 데이터를 삭제하지 못했습니다. 워크스페이스 권한을 확인한 뒤 다시 저장해 주세요.`
  );
}

export async function insertRegistryScopeRowsWithRetry<TRow extends { id: string }>(
  noun: RegistryScopeNoun,
  table: RegistryScopeTable,
  workspaceId: string,
  rows: TRow[],
  insertOnce: (nextRows: TRow[]) => PromiseLike<{
    data: unknown;
    error: PostgrestError | null;
  }>
): Promise<unknown> {
  let nextRows = rows;

  for (let attempt = 0; attempt < REGISTRY_SCOPE_INSERT_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await clearRegistryScopeRows(table, workspaceId, noun);
      nextRows = nextRows.map((row) => ({ ...row, id: createRegistryItemId() }));
    }

    const { data, error } = await insertOnce(nextRows);
    if (!error) return data;
    if (!isSupabaseUniqueViolation(error)) {
      throw new Error(formatRegistryScopeSaveError(error, noun));
    }
  }

  throw new Error(formatRegistryScopeSaveError({ code: '23505' } as PostgrestError, noun));
}
